import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Stack,
  MenuItem,
  CircularProgress,
  Chip,
  Paper,
  Divider,
  Alert,
  useTheme,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import { WhatsAppTemplate } from "../types";

interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  templates: WhatsAppTemplate[];
  recipientPhone: string;
  contactName: string;
  defaultDeceasedName?: string;
  onSendTemplate: (
    templateName: string,
    variables: string[],
    lang?: string,
    renderedBody?: string,
    imageB64?: string
  ) => Promise<void>;
  sending: boolean;
}

export const TemplateModal: React.FC<TemplateModalProps> = ({
  open,
  onClose,
  templates,
  recipientPhone,
  contactName,
  defaultDeceasedName = "",
  onSendTemplate,
  sending,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [selectedTemplateName, setSelectedTemplateName] = useState<string>("");
  const [variableValues, setVariableValues] = useState<string[]>([]);
  const [headerImageB64, setHeaderImageB64] = useState<string>("");
  const [headerImageFileName, setHeaderImageFileName] = useState<string>("");
  const [headerImageFileSize, setHeaderImageFileSize] = useState<string>("");
  const [imageError, setImageError] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset image upload states when modal closes
  useEffect(() => {
    if (!open) {
      setHeaderImageB64("");
      setHeaderImageFileName("");
      setHeaderImageFileSize("");
      setImageError("");
      setIsDragging(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateName) {
      setSelectedTemplateName(templates[0].name);
    }
  }, [templates, selectedTemplateName]);

  const selectedTemplate = templates.find((t) => t.name === selectedTemplateName);

  // Initialize smart defaults so parameters are never left empty (preventing Meta 400 error)
  useEffect(() => {
    if (selectedTemplate) {
      const initialVars = selectedTemplate.variables.map((varName, idx) => {
        const lowerName = varName.toLowerCase();
        if (
          lowerName.includes("recipient") ||
          lowerName.includes("contact") ||
          lowerName.includes("user") ||
          lowerName.includes("organizer") ||
          (idx === 0 && lowerName.includes("name"))
        ) {
          return contactName || "there";
        }
        if (lowerName.includes("deceased") || lowerName.includes("beloved")) {
          return defaultDeceasedName || "our beloved";
        }
        if (lowerName.includes("link") || lowerName.includes("url")) {
          return "https://my.muzukuru.com";
        }
        if (lowerName.includes("currency")) {
          return "USD";
        }
        if (lowerName.includes("amount")) {
          return "10.00";
        }
        if (selectedTemplate.variable_defaults && selectedTemplate.variable_defaults[idx]) {
          return selectedTemplate.variable_defaults[idx];
        }
        return "Friend";
      });
      setVariableValues(initialVars);
    } else {
      setVariableValues([]);
    }
  }, [selectedTemplate, contactName, defaultDeceasedName]);

  const handleVariableChange = (index: number, val: string) => {
    setVariableValues((prev) => {
      const next = [...prev];
      next[index] = val;
      return next;
    });
  };

  const handleProcessFile = (file: File) => {
    setImageError("");
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setImageError("Please upload a valid PNG, JPG, or WebP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image size exceeds the 5MB limit.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setHeaderImageB64(reader.result);
        setHeaderImageFileName(file.name);
        const kb = (file.size / 1024).toFixed(1);
        setHeaderImageFileSize(`${kb} KB`);
      }
    };
    reader.onerror = () => {
      setImageError("Failed to read image file. Please try again.");
    };
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleProcessFile(file);
    }
  };

  const handleRemoveImage = () => {
    setHeaderImageB64("");
    setHeaderImageFileName("");
    setHeaderImageFileSize("");
    setImageError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleProcessFile(file);
    }
  };

  const hasMissingVariables =
    selectedTemplate &&
    selectedTemplate.variables.length > 0 &&
    variableValues.some((v) => !v || !v.trim());

  const isImageRequiredAndMissing = Boolean(
    selectedTemplate?.requires_image && !headerImageB64
  );

  const handleSend = async () => {
    if (!selectedTemplateName) return;

    // Sanitize any remaining empty parameter with fallback
    const sanitizedVars = (selectedTemplate?.variables || []).map((_, idx) => {
      const val = variableValues[idx]?.trim();
      if (val) return val;
      return selectedTemplate?.variable_defaults?.[idx] || "-";
    });

    // Calculate exact rendered text
    let rendered = selectedTemplate?.body_text || selectedTemplate?.description || "";
    (selectedTemplate?.variables || []).forEach((_, idx) => {
      const val = sanitizedVars[idx];
      rendered = rendered.replace(new RegExp(`\\{\\{${idx + 1}\\}\\}`, "g"), val);
    });

    await onSendTemplate(
      selectedTemplateName,
      sanitizedVars,
      selectedTemplate?.language || "en",
      rendered,
      headerImageB64 || undefined
    );
    onClose();
  };

  // Render WhatsApp message preview with highlighted variable substitutions
  const renderPreviewElements = () => {
    if (!selectedTemplate) return null;
    const body = selectedTemplate.body_text || selectedTemplate.description;
    if (!body) return null;

    const regex = /\{\{(\d+)\}\}/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let matchKey = 0;

    while ((match = regex.exec(body)) !== null) {
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${matchKey}`}>
            {body.substring(lastIndex, match.index)}
          </span>
        );
      }

      const varNum = parseInt(match[1], 10);
      const varIdx = varNum - 1;
      const varName = selectedTemplate.variables[varIdx] || `Parameter ${varNum}`;
      const val = variableValues[varIdx]?.trim();

      if (val) {
        parts.push(
          <Box
            component="span"
            key={`var-${matchKey}`}
            sx={{
              fontWeight: 700,
              color: isDark ? "#A7F3D0" : "#065F46",
              backgroundColor: isDark
                ? "rgba(16, 185, 129, 0.25)"
                : "rgba(16, 185, 129, 0.15)",
              px: 0.6,
              py: 0.2,
              borderRadius: "4px",
              display: "inline-block",
              mx: 0.2,
            }}
          >
            {val}
          </Box>
        );
      } else {
        parts.push(
          <Box
            component="span"
            key={`missing-${matchKey}`}
            sx={{
              fontWeight: 600,
              color: isDark ? "#FCA5A5" : "#DC2626",
              backgroundColor: isDark
                ? "rgba(239, 68, 68, 0.25)"
                : "rgba(239, 68, 68, 0.12)",
              px: 0.6,
              py: 0.2,
              borderRadius: "4px",
              border: "1px dashed",
              borderColor: isDark ? "#EF4444" : "#DC2626",
              display: "inline-block",
              mx: 0.2,
            }}
          >
            [{varName}]
          </Box>
        );
      }

      lastIndex = regex.lastIndex;
      matchKey++;
    }

    if (lastIndex < body.length) {
      parts.push(<span key="text-end">{body.substring(lastIndex)}</span>);
    }

    return parts;
  };

  const currentTime = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          background: isDark ? "#111827" : "#FFFFFF",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid #E2E8F0",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          color: isDark ? "#FFFFFF" : "#0F172A",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              backgroundColor: "rgba(37, 211, 102, 0.15)",
              color: "#25D366",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.15rem" }}>
              Send Meta WhatsApp Template
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Recipient: {contactName ? `${contactName} (${recipientPhone})` : recipientPhone}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <Divider sx={{ opacity: 0.15 }} />

      <DialogContent sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          {/* Template Selector */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.8 }}>
              Select Meta Approved Template
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedTemplateName}
              onChange={(e) => setSelectedTemplateName(e.target.value)}
              disabled={sending}
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.name} value={tpl.name}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      {tpl.label}
                    </Typography>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Chip
                        label={tpl.category}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.06)",
                        }}
                      />
                      <Chip
                        label={tpl.language}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
            {selectedTemplate?.description && (
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.8, color: "text.secondary" }}
              >
                {selectedTemplate.description}
              </Typography>
            )}
          </Box>

          {/* Template Parameter Inputs */}
          {selectedTemplate && selectedTemplate.variables.length > 0 && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.2 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Template Parameters ({selectedTemplate.variables.length})
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  All placeholders required by Meta
                </Typography>
              </Stack>
              <Stack spacing={1.5}>
                {selectedTemplate.variables.map((varName, idx) => {
                  const val = variableValues[idx] || "";
                  const isBlank = !val.trim();
                  return (
                    <TextField
                      key={idx}
                      fullWidth
                      size="small"
                      label={`{{${idx + 1}}} ${varName}`}
                      value={val}
                      onChange={(e) => handleVariableChange(idx, e.target.value)}
                      disabled={sending}
                      error={isBlank}
                      helperText={isBlank ? "Required: cannot be left empty" : undefined}
                      placeholder={`Enter ${varName}`}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Header Image Upload Section (When Required) */}
          {selectedTemplate?.requires_image && (
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 0.8 }}>
                  Header Image
                  <Chip
                    label="Required by Meta"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      backgroundColor: "rgba(239, 68, 68, 0.12)",
                      color: "#dc2626",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                    }}
                  />
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  PNG, JPG, WebP up to 5MB
                </Typography>
              </Stack>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                style={{ display: "none" }}
                onChange={handleFileInputChange}
              />

              {headerImageB64 ? (
                // Uploaded Image Card
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    borderRadius: "12px",
                    backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "#F8FAFC",
                    borderColor: isDark ? "rgba(34, 197, 94, 0.4)" : "#86EFAC",
                  }}
                >
                  <Box
                    component="img"
                    src={headerImageB64}
                    alt="Uploaded Header Preview"
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "8px",
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {headerImageFileName || "Header Image Attached"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {headerImageFileSize || "Ready to send"}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleRemoveImage}
                    disabled={sending}
                    sx={{ borderRadius: "8px", textTransform: "none", fontSize: "0.78rem" }}
                  >
                    Remove
                  </Button>
                </Paper>
              ) : (
                // Dropzone Upload Area
                <Box
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => !sending && fileInputRef.current?.click()}
                  sx={{
                    p: 3,
                    border: "2px dashed",
                    borderColor: isDragging
                      ? "#25D366"
                      : isDark
                      ? "rgba(255, 255, 255, 0.18)"
                      : "#CBD5E1",
                    borderRadius: "12px",
                    backgroundColor: isDragging
                      ? isDark
                        ? "rgba(37, 211, 102, 0.12)"
                        : "rgba(37, 211, 102, 0.05)"
                      : isDark
                      ? "rgba(255, 255, 255, 0.02)"
                      : "#F8FAFC",
                    textAlign: "center",
                    cursor: sending ? "default" : "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: "#25D366",
                      backgroundColor: isDark
                        ? "rgba(37, 211, 102, 0.08)"
                        : "rgba(37, 211, 102, 0.04)",
                    },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 36, color: "#25D366", mb: 0.8 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Click or drag image here to attach header
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.3 }}>
                    Meta requires an image header parameter for this template
                  </Typography>
                </Box>
              )}

              {imageError && (
                <Typography variant="caption" sx={{ color: "#ef4444", display: "block", mt: 0.8 }}>
                  {imageError}
                </Typography>
              )}
            </Box>
          )}

          {/* Special Template Alerts */}
          {selectedTemplate?.requires_image && !headerImageB64 && (
            <Alert
              severity="warning"
              icon={<WarningAmberIcon />}
              sx={{ borderRadius: "10px", fontSize: "0.82rem" }}
            >
              <strong>Notice:</strong> This template was registered in Meta with an image header.
              Please upload an image above so Meta accepts and delivers your message.
            </Alert>
          )}

          {/* Live WhatsApp Message Preview Bubble */}
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 1 }}>
              <VisibilityIcon sx={{ fontSize: 18, color: "#25D366" }} />
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}
              >
                Live WhatsApp Message Preview (Recipient View)
              </Typography>
            </Stack>

            <Box
              sx={{
                p: 2.5,
                borderRadius: "14px",
                backgroundColor: isDark ? "#0b141a" : "#efeae2",
                border: isDark
                  ? "1px solid rgba(255, 255, 255, 0.08)"
                  : "1px solid #E2E8F0",
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
              }}
            >
              {/* WhatsApp Chat Speech Bubble */}
              <Box
                sx={{
                  maxWidth: "92%",
                  minWidth: "260px",
                  borderRadius: "12px 0px 12px 12px",
                  backgroundColor: isDark ? "#005c4b" : "#d9fdd3",
                  color: isDark ? "#E9EDEF" : "#111B21",
                  p: 1.8,
                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)",
                  position: "relative",
                }}
              >
                {/* Header Image in Preview Bubble */}
                {selectedTemplate?.requires_image && (
                  <Box sx={{ mb: 1.5 }}>
                    {headerImageB64 ? (
                      <Box
                        component="img"
                        src={headerImageB64}
                        alt="Template Header Preview"
                        sx={{
                          width: "100%",
                          maxHeight: 180,
                          objectFit: "cover",
                          borderRadius: "8px",
                          display: "block",
                          border: isDark
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid rgba(0, 0, 0, 0.08)",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          py: 2.5,
                          px: 2,
                          borderRadius: "8px",
                          border: "2px dashed",
                          borderColor: isDark ? "#ef4444" : "#dc2626",
                          backgroundColor: isDark
                            ? "rgba(239, 68, 68, 0.12)"
                            : "rgba(239, 68, 68, 0.06)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        <ImageIcon sx={{ fontSize: 28, color: "#dc2626", mb: 0.5 }} />
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, color: isDark ? "#fca5a5" : "#dc2626" }}
                        >
                          Header Image Required
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: isDark ? "rgba(255,255,255,0.7)" : "#64748b",
                          }}
                        >
                          Please upload an image above to complete this template
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.92rem",
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {renderPreviewElements()}
                </Typography>

                {/* WhatsApp Timestamp & Double Checkmarks */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  spacing={0.4}
                  sx={{ mt: 0.8 }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.45)",
                    }}
                  >
                    {currentTime}
                  </Typography>
                  <DoneAllIcon
                    sx={{
                      fontSize: 15,
                      color: isDark ? "#53bdeb" : "#53bdeb",
                    }}
                  />
                </Stack>
              </Box>
            </Box>
          </Box>
        </Stack>
      </DialogContent>

      <Divider sx={{ opacity: 0.15 }} />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={sending} sx={{ color: "text.secondary" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !selectedTemplateName || hasMissingVariables || isImageRequiredAndMissing}
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <WhatsAppIcon />}
          sx={{
            backgroundColor: "#25D366",
            "&:hover": { backgroundColor: "#1EBE5D" },
            fontWeight: 700,
            px: 3.5,
            py: 1,
            borderRadius: "10px",
            color: "#FFFFFF",
            boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
          }}
        >
          {sending ? "Sending..." : "Send WhatsApp Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
